import {
    ICorporationResponse,
    ILocationResponse,
} from '@kcftech/prometheustypescriptapiclient';

/**
 * The Modify utility type accepts the type `Type` and overrides it with
 * the type `Replace`, allowing you to specify more specific types, for
 * example, replacing `any` or `unknown` with `number` or `string`.
 *
 * Example:
 * ```ts
 *  type Generic = { x: any };
 *  type Specific = Modify<Generic, { x: number }>;
 *
 *  const processDataGeneric = ({ x }: Generic) => x.toFixed(2);
 *  const processDataSpecific = ({ x }: Specific) => x.toFixed(2);
 *
 *  processDataGeneric({ x: 1.2345 }); // OK
 *  processDataGeneric({ x: 'makes no sense' }); // OK??
 *  processDataSpecific({ x: 1.2345 }); // OK
 *
 *  processDataSpecific({ x: 'makes no sense' });
 *  //                    ^
 *  //                    ERROR: Type 'string' not assignable to 'number'.
 * ```
 */
export type Modify<Type, Replace> = Omit<Type, keyof Replace> & Replace;

/**
 * Type for creating a readonly lookup object.
 *
 * @example
 * ```ts
 * export type Thing = 'foo' | 'bar' | 'baz';
 *
 * export const formattedThings: Lookup<Thing, string> = {
 *     foo: 'Foo',
 *     bar: 'Bar',
 *     baz: 'Baz',
 * };
 * ```
 */
export type Lookup<TKey extends string | number | symbol, TValue> = Readonly<
    Record<TKey, TValue>
>;

export type MouseOrKeyboardEvent<T extends HTMLElement | SVGSVGElement = HTMLElement> =
    | React.KeyboardEvent<T>
    | React.MouseEvent<T, MouseEvent>;

export type DateRange = Readonly<{
    startDate: Date;
    endDate: Date;
}>;

export type SystemTimeRange = Readonly<{
    /**
     * Time range selected from Corporation time ranges.
     */
    type: 'system';

    /**
     * The id of the time range.
     */
    id: string;

    /**
     * Length of time range in seconds.
     */
    timeInSeconds: number;
}>;

export type CustomTimeRange = Readonly<{
    /**
     * Time range is custom.
     */
    type: 'custom';

    /**
     * The id of the time range.
     */
    id: string;

    /**
     * The start time of data displayed in plots.
     */
    startTimestamp: number;

    /**
     * The end time of data displayed in plots.
     */
    endTimestamp: number;
}>;

/**
 * Typed time range.
 */
export type TimeRange = SystemTimeRange | CustomTimeRange;

/**
 * Utility to recursively modify a type to be mutable.
 */
export declare type Mutable<T> = {
    -readonly [K in keyof T]: Mutable<T[K]>;
};

export type ContextualPageProps = Readonly<{
    /**
     * The corporation from the top bar context, if there is one.
     */
    corporation?: ICorporationResponse;
    /**
     * The location from the top bar context, if there is one.
     */
    location?: ILocationResponse;
}>;

/**
 * Construct a type with the properties of T, with the properties specified by K
 * made optional.
 */
export type OptionalProps<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export type RequireDefined<T> = { [P in keyof T]: Exclude<T[P], undefined> };

export type RequiredButAllowUndefined<T> = {
    [K in keyof Required<T>]: T[K];
};

export const typeArrayOfAll = <T>() => <U extends T[]>(
    array: U & ([T] extends [U[number]] ? unknown : 'Invalid')
) => array;

/**
 * Enum for HTTP methods used in v4 fetch requests.
 */
export enum HttpMethod {
    Get = 'GET',
    Post = 'POST',
    Put = 'PUT',
    Patch = 'PATCH',
    Delete = 'DELETE',
}

export type Prefixed<T, P extends string> = {
    [K in keyof T as K extends string ? `${P}${K}` : never]: T[K];
};

export type PickWithPrefix<T, K extends keyof T, PX extends string> = Prefixed<
    Pick<T, K>,
    PX
>;
export type PickForStyled<T, K extends keyof T> = PickWithPrefix<T, K, '$'>;
